import { useState, useEffect } from "react";
import { X, ShoppingBag, Heart, Star, ArrowRight, Plus, Minus, Truck, RefreshCw, Shield, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { NavLink } from "react-router-dom";
import { useQuickView } from "@/lib/quickViewContext";
import { useCart } from "@/lib/cartContext";
import { useWishlist } from "@/lib/wishlistContext";
import { useToast } from "@/lib/toastContext";
import { cn } from "@/lib/utils";

const badgeStyle: Record<string, string> = {
  SALE: "bg-red-600",
  NEW: "bg-stone-900",
  "BEST SELLER": "bg-emerald-600",
};

const colorHex: Record<string, string> = {
  nero: "#1c1c1c", bianco: "#f5f5f5", avorio: "#fffff0", grigio: "#9e9e9e",
  navy: "#001f5b", caramello: "#c68642", cipria: "#f2c8c4", cognac: "#8b4513",
  bordeaux: "#4a0018", beige: "#f5f0e8", verde: "#2d4a3e", oro: "#b8973a",
  nude: "#e8c9a0", blu: "#1e3a5f", azzurro: "#6fa8dc", cammello: "#c19a6b",
};

export function QuickViewModal() {
  const { product, closeQuickView } = useQuickView();
  const { addItem } = useCart();
  const { toggle, has } = useWishlist();
  const { showToast } = useToast();

  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [selectedImg, setSelectedImg] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const allImages = product ? [product.image, ...(product.images ?? [])] : [];

  useEffect(() => {
    setSelectedSize(null);
    setSelectedColor(null);
    setQty(1);
    setAdded(false);
    setSelectedImg(0);
    setLightboxOpen(false);
  }, [product?.id]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (lightboxOpen) { setLightboxOpen(false); return; }
        closeQuickView();
        return;
      }
      if (e.key === "ArrowRight") setSelectedImg((i) => (i + 1) % allImages.length);
      if (e.key === "ArrowLeft") setSelectedImg((i) => (i - 1 + allImages.length) % allImages.length);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen, allImages.length, closeQuickView]);

  if (!product) return null;

  const wished = has(product.id);
  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : null;
  const needsSize = product.sizes && product.sizes.length > 0 && !selectedSize;

  function handleAdd() {
    if (needsSize) return;
    addItem(product!, qty, selectedSize ?? undefined, selectedColor ?? undefined);
    showToast(product!);
    setAdded(true);
    setTimeout(() => { setAdded(false); closeQuickView(); }, 1200);
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60] bg-stone-900/70 backdrop-blur-sm"
        onClick={closeQuickView}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center md:p-6 pointer-events-none">
        <div
          className="relative bg-white w-full md:max-w-5xl max-h-[95vh] md:max-h-[88vh] overflow-hidden shadow-2xl pointer-events-auto flex flex-col md:flex-row rounded-t-2xl md:rounded-none"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close */}
          <button
            onClick={closeQuickView}
            className="absolute top-4 right-4 z-20 size-9 bg-white shadow-md flex items-center justify-center text-stone-400 hover:text-stone-900 transition-colors"
          >
            <X className="size-4" />
          </button>

          {/* ── LEFT: Gallery ── */}
          <div className="md:w-[52%] flex-shrink-0 bg-stone-50 overflow-y-auto">
            <div className="p-4 h-full flex flex-col">
              <div className="flex gap-2 flex-1 min-h-0">
                {/* Thumbnails verticali — desktop only */}
                {allImages.length > 1 && (
                  <div className="hidden md:flex flex-col gap-1.5 flex-shrink-0">
                    {allImages.map((src, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedImg(i)}
                        className={cn(
                          "w-[56px] aspect-[3/4] overflow-hidden flex-shrink-0 transition-all",
                          selectedImg === i
                            ? "ring-2 ring-stone-900"
                            : "opacity-50 hover:opacity-90 ring-1 ring-stone-200"
                        )}
                      >
                        <img src={src} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}

                {/* Immagine principale */}
                <div className="relative flex-1 min-w-0">
                  <div
                    className="aspect-[3/4] overflow-hidden bg-stone-100 cursor-zoom-in group"
                    onClick={() => setLightboxOpen(true)}
                  >
                    <img
                      key={selectedImg}
                      src={allImages[selectedImg]}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center pointer-events-none">
                      <ZoomIn className="size-7 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                    </div>
                  </div>

                  {product.badge && (
                    <span className={cn("absolute top-3 left-3 text-white text-[10px] font-semibold px-2.5 py-1 tracking-widest uppercase", badgeStyle[product.badge])}>
                      {product.badge === "BEST SELLER" ? "Best" : product.badge}
                      {discount ? ` –${discount}%` : ""}
                    </span>
                  )}

                  <button
                    onClick={() => toggle(product.id)}
                    className={cn(
                      "absolute top-3 right-3 size-9 bg-white flex items-center justify-center shadow-sm hover:shadow-md transition-all",
                      wished ? "opacity-100" : "opacity-80 hover:opacity-100"
                    )}
                  >
                    <Heart className={cn("size-4", wished ? "fill-red-500 text-red-500" : "text-stone-400")} />
                  </button>

                  {allImages.length > 1 && (
                    <span className="absolute bottom-3 right-3 bg-black/50 text-white text-[10px] tracking-wider px-2 py-1">
                      {selectedImg + 1} / {allImages.length}
                    </span>
                  )}
                </div>
              </div>

              {/* Thumbnail strip mobile */}
              {allImages.length > 1 && (
                <div className="md:hidden flex gap-2 mt-2 overflow-x-auto [&::-webkit-scrollbar]:hidden pb-1">
                  {allImages.map((src, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedImg(i)}
                      className={cn(
                        "w-14 aspect-[3/4] overflow-hidden flex-shrink-0 transition-all",
                        selectedImg === i
                          ? "ring-2 ring-stone-900"
                          : "opacity-50 hover:opacity-90 ring-1 ring-stone-200"
                      )}
                    >
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── RIGHT: Info ── */}
          <div className="flex-1 overflow-y-auto p-6 flex flex-col">
            <p className="text-[10px] tracking-[0.3em] uppercase text-emerald-600 font-semibold mb-2">
              {product.brand || product.category}
            </p>
            <h2 className="font-semibold text-lg sm:text-xl md:text-3xl md:font-bold text-stone-900 tracking-tight mb-3 leading-snug">
              {product.name}
            </h2>

            {/* Rating */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={cn("size-3.5", i < Math.floor(product.rating) ? "fill-amber-400 text-amber-400" : "text-stone-200")}
                  />
                ))}
              </div>
              <span className="text-xs text-stone-400">{product.rating} · {product.reviews} recensioni</span>
            </div>

            {/* Price */}
            <div className="flex items-center gap-3 mb-5 pb-5 border-b border-stone-100">
              <span className="text-2xl font-light text-stone-900">€{product.price.toLocaleString("it-IT")}</span>
              {product.originalPrice && (
                <span className="text-base text-stone-400 line-through">€{product.originalPrice.toLocaleString("it-IT")}</span>
              )}
              {discount && <span className="text-sm text-red-600 font-medium">–{discount}%</span>}
            </div>

            {/* Description */}
            <p className="text-stone-500 text-sm leading-relaxed mb-5 line-clamp-3">
              {product.longDescription}
            </p>

            {/* Material */}
            {product.material && (
              <div className="mb-4 pb-4 border-b border-stone-100">
                <span className="text-[10px] tracking-widest uppercase text-stone-400">Materiale</span>
                <p className="text-sm text-stone-700 mt-1">{product.material}</p>
              </div>
            )}

            {/* Colors */}
            {product.colors && product.colors.length > 0 && (
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-[10px] tracking-widest uppercase text-stone-600 font-medium">Colore</span>
                  {selectedColor && <span className="text-[10px] text-stone-500 capitalize">{selectedColor}</span>}
                </div>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((color) => {
                    const hex = colorHex[color.toLowerCase()];
                    const isSelected = selectedColor === color;
                    return (
                      <button
                        key={color}
                        onClick={() => setSelectedColor(isSelected ? null : color)}
                        title={color.charAt(0).toUpperCase() + color.slice(1)}
                        className={cn(
                          "size-7 rounded-full transition-all flex-shrink-0",
                          isSelected
                            ? "ring-2 ring-offset-2 ring-stone-900"
                            : "ring-1 ring-stone-200 hover:ring-stone-400"
                        )}
                        style={{ background: hex ?? "#ccc" }}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sizes */}
            {product.sizes && product.sizes.length > 0 && (
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-[10px] tracking-widest uppercase text-stone-600 font-medium">Taglia</span>
                  {!selectedSize && <span className="text-[10px] text-red-500">Seleziona una taglia</span>}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={cn(
                        "min-w-[2.75rem] px-3 py-2 text-xs border transition-colors",
                        selectedSize === size
                          ? "bg-stone-900 text-white border-stone-900"
                          : "bg-white text-stone-600 border-stone-200 hover:border-stone-500"
                      )}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Qty */}
            <div className="flex items-center gap-3 mb-5">
              <span className="text-[10px] tracking-widest uppercase text-stone-600 font-medium">Quantità</span>
              <div className="flex items-center border border-stone-200 bg-white">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="px-3 py-2.5 text-stone-500 hover:text-stone-900 transition-colors"
                >
                  <Minus className="size-3" />
                </button>
                <span className="px-4 py-2.5 text-sm font-medium text-stone-900 min-w-10 text-center border-x border-stone-200">
                  {qty}
                </span>
                <button
                  onClick={() => setQty(qty + 1)}
                  className="px-3 py-2.5 text-stone-500 hover:text-stone-900 transition-colors"
                >
                  <Plus className="size-3" />
                </button>
              </div>
            </div>

            {/* CTAs */}
            <div className="mt-auto space-y-2.5">
              <button
                onClick={handleAdd}
                disabled={!!needsSize}
                className={cn(
                  "w-full py-4 text-xs tracking-widest uppercase font-semibold flex items-center justify-center gap-2 transition-colors",
                  added
                    ? "bg-emerald-600 text-white"
                    : needsSize
                    ? "bg-stone-100 text-stone-400 cursor-not-allowed"
                    : "bg-stone-900 text-white hover:bg-stone-700"
                )}
              >
                <ShoppingBag className="size-4" />
                {added ? "Aggiunto!" : needsSize ? "Seleziona la taglia" : "Aggiungi al carrello"}
              </button>

              <NavLink
                to={`/product/${product.id}`}
                onClick={closeQuickView}
                className="group w-full py-3.5 text-[10px] tracking-widest uppercase font-medium text-center border border-stone-200 text-stone-600 hover:border-stone-900 hover:text-stone-900 transition-colors flex items-center justify-center gap-2"
              >
                Vedi prodotto completo
                <ArrowRight className="size-3 group-hover:translate-x-0.5 transition-transform" />
              </NavLink>
            </div>

            {/* Perks */}
            <div className="mt-6 pt-5 border-t border-stone-100 grid grid-cols-3 gap-3 text-center">
              {[
                { icon: Truck, label: "Spedizione", sub: "Express 24-48h" },
                { icon: RefreshCw, label: "Reso", sub: "Entro 14 giorni" },
                { icon: Shield, label: "Autenticità", sub: "Certificata" },
              ].map(({ icon: Icon, label, sub }) => (
                <div key={label}>
                  <Icon className="size-4 text-stone-400 mx-auto mb-1.5" />
                  <p className="text-[10px] tracking-wider uppercase font-semibold text-stone-700">{label}</p>
                  <p className="text-[10px] text-stone-400 mt-0.5">{sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[70] bg-black/95 flex flex-col"
          onClick={() => setLightboxOpen(false)}
        >
          <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <span className="text-white/60 text-sm tracking-wider">{selectedImg + 1} / {allImages.length}</span>
            <button onClick={() => setLightboxOpen(false)} className="text-white/70 hover:text-white transition-colors">
              <X className="size-6" />
            </button>
          </div>

          <div className="flex-1 flex items-center justify-center px-4 min-h-0 relative" onClick={(e) => e.stopPropagation()}>
            {allImages.length > 1 && (
              <button
                onClick={() => setSelectedImg((i) => (i - 1 + allImages.length) % allImages.length)}
                className="absolute left-4 md:left-8 z-10 size-12 bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="size-6 text-white" />
              </button>
            )}
            <img
              key={selectedImg}
              src={allImages[selectedImg]}
              alt={product.name}
              className="max-h-full max-w-full object-contain"
              style={{ maxHeight: "calc(100vh - 160px)" }}
            />
            {allImages.length > 1 && (
              <button
                onClick={() => setSelectedImg((i) => (i + 1) % allImages.length)}
                className="absolute right-4 md:right-8 z-10 size-12 bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <ChevronRight className="size-6 text-white" />
              </button>
            )}
          </div>

          {allImages.length > 1 && (
            <div
              className="flex-shrink-0 flex justify-center gap-2 px-4 py-4 overflow-x-auto [&::-webkit-scrollbar]:hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {allImages.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedImg(i)}
                  className={cn(
                    "w-14 aspect-[3/4] overflow-hidden flex-shrink-0 transition-all",
                    selectedImg === i ? "ring-2 ring-white opacity-100" : "opacity-40 hover:opacity-70"
                  )}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
